import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateBR } from "@/utils/dateUtils";
import type { WhatsAppMetricas as WhatsAppMetricasData } from "@/types/whatsapp";

// Paleta categórica validada (validate_palette.js: banda de lightness, chroma,
// separação CVD e contraste passam nos dois modos). Não trocar sem revalidar.
const CHART_CONFIG = {
  mediana: {
    label: "Mediana",
    theme: { light: "#1d4ed8", dark: "#3b82f6" },
  },
  p90: {
    label: "P90",
    theme: { light: "#ea580c", dark: "#ea580c" },
  },
  sem_resposta: {
    label: "Sem resposta",
    theme: { light: "#ea580c", dark: "#ea580c" },
  },
  disponibilidade: {
    label: "Disponibilidade",
    theme: { light: "#0d9488", dark: "#0d9488" },
  },
  negociacao: {
    label: "Negociação",
    theme: { light: "#1d4ed8", dark: "#3b82f6" },
  },
  sentimento: {
    label: "Sentimento",
    theme: { light: "#be185d", dark: "#db2777" },
  },
};

function StatTile({
  valor,
  unidade,
  rotulo,
  alerta,
}: {
  valor: string | number;
  unidade?: string;
  rotulo: string;
  alerta?: boolean;
}) {
  return (
    <div>
      <div className={`text-2xl font-semibold ${alerta ? "text-red-700" : ""}`}>
        {valor}
        {unidade && (
          <span className="text-sm font-normal text-muted-foreground"> {unidade}</span>
        )}
      </div>
      <div className="text-sm text-muted-foreground">{rotulo}</div>
    </div>
  );
}

export function WhatsAppMetricas({ dados }: { dados: WhatsAppMetricasData }) {
  const series = dados.series ?? [];
  const ultima = series.length > 0 ? series[series.length - 1] : null;

  const dadosGrafico = useMemo(
    () =>
      series.map((ponto) => ({
        ...ponto,
        rotulo: `#${ponto.numero}`,
        janela: `${formatDateBR(ponto.window_start)} a ${formatDateBR(ponto.window_end)}`,
      })),
    [series],
  );

  const semResposta = useMemo(
    () =>
      (dados.por_conversa_ultima ?? [])
        .filter((conversa) => conversa.sem_resposta)
        .sort((a, b) => b.msgs_contato - a.msgs_contato),
    [dados.por_conversa_ultima],
  );

  if (!ultima) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        As métricas aparecem depois do primeiro processamento.
      </div>
    );
  }

  const totalMensagens =
    ultima.msgs_contato + ultima.msgs_atendente + ultima.msgs_ia;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Última rodada (#{ultima.numero}) · {formatDateBR(ultima.window_start)} a{" "}
            {formatDateBR(ultima.window_end)}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <StatTile
            valor={ultima.mediana_primeira_resposta_min ?? "-"}
            unidade="min"
            rotulo="Mediana da 1ª resposta"
          />
          <StatTile
            valor={ultima.p90_primeira_resposta_min ?? "-"}
            unidade="min"
            rotulo="P90 da 1ª resposta"
          />
          <StatTile
            valor={ultima.conversas_sem_resposta}
            rotulo="Conversas sem resposta"
            alerta={ultima.conversas_sem_resposta > 0}
          />
          <StatTile valor={ultima.conversas} rotulo="Conversas analisadas" />
          <StatTile valor={totalMensagens} rotulo="Mensagens na janela" />
        </CardContent>
      </Card>

      {series.length < 2 ? (
        <Card>
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            Os gráficos de evolução aparecem a partir da segunda rodada
            processada.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Tempo até a primeira resposta
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Minutos entre a mensagem do contato e a resposta da equipe, por
                rodada.
              </p>
            </CardHeader>
            <CardContent>
              <ChartContainer config={CHART_CONFIG} className="h-[260px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={dadosGrafico}
                    margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="rotulo"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      width={38}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 11 }}
                      unit=" min"
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="mediana_primeira_resposta_min"
                      name="Mediana"
                      stroke="var(--color-mediana)"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="p90_primeira_resposta_min"
                      name="P90"
                      stroke="var(--color-p90)"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Conversas sem resposta</CardTitle>
              <p className="text-sm text-muted-foreground">
                Conversas cuja última mensagem na janela foi do contato.
              </p>
            </CardHeader>
            <CardContent>
              <ChartContainer config={CHART_CONFIG} className="h-[260px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dadosGrafico}
                    margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="rotulo"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      width={32}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey="conversas_sem_resposta"
                      name="Sem resposta"
                      fill="var(--color-sem_resposta)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={44}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Extrações por rodada</CardTitle>
              <p className="text-sm text-muted-foreground">
                Registros criados em cada processamento, por categoria.
              </p>
            </CardHeader>
            <CardContent>
              <ChartContainer config={CHART_CONFIG} className="h-[260px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dadosGrafico}
                    margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="rotulo"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      width={32}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar
                      dataKey="disponibilidade"
                      stackId="itens"
                      name="Disponibilidade"
                      fill="var(--color-disponibilidade)"
                      maxBarSize={44}
                    />
                    <Bar
                      dataKey="negociacao"
                      stackId="itens"
                      name="Negociação"
                      fill="var(--color-negociacao)"
                      maxBarSize={44}
                    />
                    <Bar
                      dataKey="sentimento"
                      stackId="itens"
                      name="Sentimento"
                      fill="var(--color-sentimento)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={44}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {semResposta.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Conversas sem resposta na última rodada ({semResposta.length})
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              A última mensagem foi do contato e ninguém respondeu na janela.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contato</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead className="text-right">Mensagens do contato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {semResposta.map((conversa) => (
                  <TableRow key={conversa.conversation_id}>
                    <TableCell className="font-medium">
                      {conversa.contact_name ?? "Sem nome"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {conversa.telefone ?? "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {conversa.msgs_contato}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
